import { Text } from "@clipboard-health/ui-components";
import { Stack, type SxProps, type Theme } from "@mui/material";
import { type EmergencyCreditStatsCommon } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { type ReactElement } from "react";

import { getCreditProgressBarLabel } from "../utils/getCreditProgressBarLabel";
import { CreditProgressBar } from "./CreditProgressBar";

export interface CancellationCreditProgressBarProps {
  cancellationCreditStats: EmergencyCreditStatsCommon;
  /**
   * Whether to show the "X/Y shifts complete" text below the progress bar
   * @default true
   */
  showShiftsProgress?: boolean;
  /**
   * Custom sx prop for the progress bar
   */
  sx?: SxProps<Theme>;
}

export function CancellationCreditProgressBar(
  props: CancellationCreditProgressBarProps
): ReactElement {
  const { cancellationCreditStats, showShiftsProgress = true, sx } = props;
  const { completedShiftsTowardsNextCredit, requiredShiftsForNextCredit, totalAvailableCredits } =
    cancellationCreditStats;

  const hasCredit = totalAvailableCredits > 0;
  // If credit is available but no progress yet, show the bar as complete
  const isCreditAvailableWithNoProgress = hasCredit && completedShiftsTowardsNextCredit === 0;
  const completedCount = isCreditAvailableWithNoProgress
    ? requiredShiftsForNextCredit
    : completedShiftsTowardsNextCredit;

  return (
    <Stack spacing={1} alignItems="center">
      <CreditProgressBar
        completedCount={completedCount}
        requiredCount={requiredShiftsForNextCredit}
        label={getCreditProgressBarLabel("cancellation", hasCredit)}
        color="blue"
        sx={sx}
      />
      {showShiftsProgress && !isCreditAvailableWithNoProgress && (
        <Text semibold variant="caption" color={(theme) => theme.palette.text.tertiary}>
          {completedShiftsTowardsNextCredit}/{requiredShiftsForNextCredit} shifts complete
        </Text>
      )}
    </Stack>
  );
}
